// packages/supabase-client/src/react/hooks.ts

import { useContext, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { AuthError } from '@supabase/supabase-js';
import { SupabaseAuthContext, SupabaseAuthContextValue } from './SupabaseAuthProvider';
import { mapAuthError, AuthErrorCode } from '../errorMapping';
import { useAuthUI, AuthUITextValue, AuthUIErrorTextOverrides } from './ui';
import { defaultAuthCopyByI18nKey } from './defaultCopy';

/**
 * The core hook to access the Supabase auth context.
 * Throws an error if used outside of a SupabaseAuthProvider.
 */
export const useSupabaseAuth = (): SupabaseAuthContextValue => {
  const context = useContext(SupabaseAuthContext);
  if (context === undefined) {
    throw new Error('useSupabaseAuth must be used within a SupabaseAuthProvider');
  }
  return context;
};

// --- EXISTING HOOKS (No Changes) ---
export const useSupabaseClient = () => useSupabaseAuth().supabaseClient;
export const useSession = () => useSupabaseAuth().session;
export const useUser = () => useSupabaseAuth().session?.user ?? null;
export const useIsLoading = () => useSupabaseAuth().isLoading;

// --- NEW HOOK ---
/**
 * A hook to get the instantiated AuthService instance from the context.
 * This is the correct way to access auth methods from within the provider tree.
 */
export const useAuthService = () => useSupabaseAuth().authService;

const isDefinedText = (value: AuthUITextValue | undefined | null): value is AuthUITextValue => value !== undefined && value !== null;
const isStringLike = (value: AuthUITextValue | undefined | null): value is string | number =>
  typeof value === 'string' || typeof value === 'number';

export const useAuthText = () => {
  const { t } = useTranslation(['auth', 'common']);
  const { text } = useAuthUI();

  const getDefaultValue = (
    i18nKey?: string,
    options?: Record<string, unknown>,
    fallback?: AuthUITextValue | string
  ): string | undefined => {
    const optionDefaultValue = options?.defaultValue;
    if (typeof optionDefaultValue === 'string' || typeof optionDefaultValue === 'number') {
      return String(optionDefaultValue);
    }
    if (typeof fallback === 'string' || typeof fallback === 'number') {
      return String(fallback);
    }
    return i18nKey ? defaultAuthCopyByI18nKey[i18nKey] : undefined;
  };

  const translateWithDefault = (
    i18nKey: string,
    options?: Record<string, unknown>,
    fallback?: AuthUITextValue | string
  ) => {
    const defaultValue = getDefaultValue(i18nKey, options, fallback);
    const translated = t(i18nKey, defaultValue ? { ...options, defaultValue } : options);
    if (translated === i18nKey && fallback !== undefined) {
      return fallback;
    }
    return translated;
  };

  const resolveText = (
    override: AuthUITextValue | undefined,
    i18nKey?: string,
    options?: Record<string, unknown>,
    fallback?: AuthUITextValue
  ): AuthUITextValue => {
    if (isDefinedText(override)) return override;
    if (i18nKey) return translateWithDefault(i18nKey, options, fallback);
    return fallback ?? '';
  };

  const resolveString = (
    override: AuthUITextValue | undefined,
    i18nKey?: string,
    options?: Record<string, unknown>,
    fallback?: string
  ): string => {
    if (isStringLike(override)) return String(override);
    if (i18nKey) {
      const translated = translateWithDefault(i18nKey, options, fallback);
      if (isStringLike(translated)) return String(translated);
    }
    return fallback ?? '';
  };

  return { text, resolveText, resolveString };
};

export type AuthFlowStatus = 'idle' | 'pending' | 'success' | 'error';

export interface AuthFlowState {
  status: AuthFlowStatus;
  error: ReactNode | null;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  start: () => void;
  succeed: () => void;
  fail: (error: ReactNode) => void;
  reset: () => void;
  setError: (error: ReactNode | null) => void;
}

export const useAuthFlow = (): AuthFlowState => {
  const [status, setStatus] = useState<AuthFlowStatus>('idle');
  const [error, setErrorState] = useState<ReactNode | null>(null);

  const start = () => {
    setStatus('pending');
    setErrorState(null);
  };

  const succeed = () => {
    setStatus('success');
  };

  const fail = (nextError: ReactNode) => {
    setStatus('error');
    setErrorState(nextError);
  };

  const reset = () => {
    setStatus('idle');
    setErrorState(null);
  };

  const setError = (nextError: ReactNode | null) => {
    setErrorState(nextError);
    setStatus(nextError ? 'error' : 'idle');
  };

  return {
    status,
    error,
    isPending: status === 'pending',
    isSuccess: status === 'success',
    isError: status === 'error',
    start,
    succeed,
    fail,
    reset,
    setError,
  };
};

const errorOverrideKeyByCode: Record<AuthErrorCode, keyof AuthUIErrorTextOverrides> = {
  invalid_credentials: 'invalidCredentials',
  user_already_registered: 'userAlreadyRegistered',
  email_not_confirmed: 'emailNotConfirmed',
  invalid_email: 'invalidEmail',
  weak_password: 'weakPassword',
  password_reset_failed: 'passwordResetFailed',
  magic_link_failed: 'magicLinkFailed',
  oauth_failed: 'oauthFailed',
  rate_limited: 'rateLimited',
  network: 'network',
  session_expired: 'sessionExpired',
  unknown: 'unknown',
};

const errorI18nKeyByCode: Record<AuthErrorCode, string> = {
  invalid_credentials: 'auth:errors.invalidCredentials',
  user_already_registered: 'auth:errors.userAlreadyRegistered',
  email_not_confirmed: 'auth:errors.emailNotConfirmed',
  invalid_email: 'auth:errors.invalidEmail',
  weak_password: 'auth:errors.weakPassword',
  password_reset_failed: 'auth:errors.passwordResetFailed',
  magic_link_failed: 'auth:errors.magicLinkFailed',
  oauth_failed: 'auth:errors.oauthFailed',
  rate_limited: 'auth:errors.rateLimited',
  network: 'auth:errors.networkError',
  session_expired: 'auth:errors.sessionExpired',
  unknown: 'auth:errors.unknownError',
};

export const useAuthErrorMessage = () => {
  const { t } = useTranslation(['auth', 'common']);
  const { text } = useAuthUI();

  const resolveErrorMessage = (error?: AuthError | null): AuthUITextValue => {
    const mapped = mapAuthError(error ?? null);
    const overrideKey = errorOverrideKeyByCode[mapped.code];
    const overrideValue = overrideKey ? text.errors?.[overrideKey] : undefined;
    if (isDefinedText(overrideValue)) return overrideValue;

    const i18nKey = errorI18nKeyByCode[mapped.code];
    return i18nKey ? t(i18nKey, { defaultValue: mapped.message }) : mapped.message;
  };

  return { resolveErrorMessage };
};

export { useAuthUI };
